import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircleIcon, UploadCloudIcon, FileTextIcon, UserIcon, CalendarIcon, ActivityIcon, AlertCircleIcon, ChevronRightIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface GoalGuardClaimProps {
  navigate: (screen: string) => void;
}
export function GoalGuardClaim({
  navigate
}: GoalGuardClaimProps) {
  const [step, setStep] = useState(0);
  const [claimType, setClaimType] = useState<'death' | 'disability' | null>(null);
  // Form State
  const [formData, setFormData] = useState({
    deceasedName: '',
    dateOfDeath: '',
    relationship: '',
    disabilityDesc: '',
    incidentDate: '',
    medicalStatus: ''
  });
  // Upload State
  const [uploads, setUploads] = useState({
    deathCert: false,
    validId: false,
    medicalReport: false,
    disabilityCert: false
  });
  const [acceptedDeclaration, setAcceptedDeclaration] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleNext = () => {
    if (step === 3) {
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setStep(4);
      }, 2000);
    } else {
      setStep(prev => prev + 1);
    }
  };
  const renderStepIndicator = () => {
    if (step >= 4) return null;
    const steps = ['Type', 'Details', 'Uploads', 'Review'];
    return <div className="flex items-center justify-between mb-8 px-2 relative" data-id="element-1931">
        <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-1 bg-gray-200 -z-10 rounded-full" data-id="element-1932"></div>
        <motion.div className="absolute left-6 top-1/2 -translate-y-1/2 h-1 bg-[#2BA89D] -z-10 rounded-full" animate={{
        width: `${step / (steps.length - 1) * 100}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-1933" />
        {steps.map((s, i) => <div key={s} className="flex flex-col items-center gap-2 bg-slate-50 px-1" data-id="element-1934">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${i < step ? 'bg-[#2BA89D] border-[#2BA89D] text-white' : i === step ? 'bg-white border-[#2BA89D] text-[#2BA89D]' : 'bg-white border-gray-300 text-gray-400'}`} data-id="element-1935">
              {i < step ? <CheckCircleIcon className="w-5 h-5" data-id="element-1936" /> : i + 1}
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${i <= step ? 'text-[#0D3B3B]' : 'text-gray-400'}`} data-id="element-1937">
              {s}
            </span>
          </div>)}
      </div>;
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col h-full" data-id="element-1938">
      <TopBar title={step >= 4 ? 'Claim Status' : 'Make a Claim'} showBack onBack={() => {
      if (step > 0 && step < 4) setStep(step - 1);else navigate('policies');
    }} data-id="element-1939" />

      <div className="px-5 pt-6 pb-8 flex-1 flex flex-col" data-id="element-1940">
        {renderStepIndicator()}

        <AnimatePresence mode="wait" data-id="element-1941">
          {/* STEP 0: Claim Type Selection */}
          {step === 0 && <motion.div key="step0" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1" data-id="element-1942">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1943">
                Select Claim Type
              </h2>
              <p className="text-sm text-gray-500 mb-8" data-id="element-1944">
                What type of claim are you making for your GoalGuard policy?
              </p>

              <div className="space-y-4" data-id="element-1945">
                <button onClick={() => setClaimType('death')} className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${claimType === 'death' ? 'border-[#2BA89D] bg-[#2BA89D]/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1946">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${claimType === 'death' ? 'bg-[#2BA89D] text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1947">
                    <UserIcon className="w-6 h-6" data-id="element-1948" />
                  </div>
                  <div className="flex-1" data-id="element-1949">
                    <h3 className="font-bold text-[#0D3B3B] text-lg" data-id="element-1950">
                      Death Claim
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1951">
                      Claim due to the passing of the policyholder.
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${claimType === 'death' ? 'border-[#2BA89D]' : 'border-gray-300'}`} data-id="element-1952">
                    {claimType === 'death' && <div className="w-3 h-3 bg-[#2BA89D] rounded-full" data-id="element-1953" />}
                  </div>
                </button>

                <button onClick={() => setClaimType('disability')} className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${claimType === 'disability' ? 'border-[#2BA89D] bg-[#2BA89D]/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1954">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${claimType === 'disability' ? 'bg-[#2BA89D] text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1955">
                    <ActivityIcon className="w-6 h-6" data-id="element-1956" />
                  </div>
                  <div className="flex-1" data-id="element-1957">
                    <h3 className="font-bold text-[#0D3B3B] text-lg" data-id="element-1958">
                      Permanent Disability
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1959">
                      Claim due to permanent injury or disability.
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${claimType === 'disability' ? 'border-[#2BA89D]' : 'border-gray-300'}`} data-id="element-1960">
                    {claimType === 'disability' && <div className="w-3 h-3 bg-[#2BA89D] rounded-full" data-id="element-1961" />}
                  </div>
                </button>
              </div>

              <div className="mt-auto pt-8" data-id="element-1962">
                <button onClick={handleNext} disabled={!claimType} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${claimType ? 'bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-1963">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* STEP 1: Form Fields */}
          {step === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-1964">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1965">
                Claim Details
              </h2>
              <p className="text-sm text-gray-500 mb-8" data-id="element-1966">
                Please provide the required details for the{' '}
                {claimType === 'death' ? 'death' : 'disability'} claim.
              </p>

              <div className="space-y-5 flex-1" data-id="element-1967">
                {claimType === 'death' ? <>
                    <div data-id="element-1968">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1969">
                        Deceased Full Name
                      </label>
                      <input type="text" value={formData.deceasedName} onChange={e => setFormData({
                  ...formData,
                  deceasedName: e.target.value
                })} placeholder="Enter full name" className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1970" />
                    </div>
                    <div data-id="element-1971">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1972">
                        Date of Death
                      </label>
                      <div className="relative" data-id="element-1973">
                        <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1974" />
                        <input type="date" value={formData.dateOfDeath} onChange={e => setFormData({
                    ...formData,
                    dateOfDeath: e.target.value
                  })} className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1975" />
                      </div>
                    </div>
                    <div data-id="element-1976">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1977">
                        Relationship to Policyholder
                      </label>
                      <select value={formData.relationship} onChange={e => setFormData({
                  ...formData,
                  relationship: e.target.value
                })} className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1978">
                        <option value="" disabled data-id="element-1979">
                          Select relationship
                        </option>
                        <option value="spouse" data-id="element-1980">Spouse</option>
                        <option value="child" data-id="element-1981">Child</option>
                        <option value="parent" data-id="element-1982">Parent</option>
                        <option value="sibling" data-id="element-1983">Sibling</option>
                        <option value="other" data-id="element-1984">Other</option>
                      </select>
                    </div>
                  </> : <>
                    <div data-id="element-1985">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1986">
                        Injury/Disability Description
                      </label>
                      <textarea value={formData.disabilityDesc} onChange={e => setFormData({
                  ...formData,
                  disabilityDesc: e.target.value
                })} placeholder="Describe the nature of the disability" rows={3} className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D] resize-none" data-id="element-1987" />
                    </div>
                    <div data-id="element-1988">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1989">
                        Incident Date
                      </label>
                      <div className="relative" data-id="element-1990">
                        <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1991" />
                        <input type="date" value={formData.incidentDate} onChange={e => setFormData({
                    ...formData,
                    incidentDate: e.target.value
                  })} className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1992" />
                      </div>
                    </div>
                    <div data-id="element-1993">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1994">
                        Current Medical Status
                      </label>
                      <input type="text" value={formData.medicalStatus} onChange={e => setFormData({
                  ...formData,
                  medicalStatus: e.target.value
                })} placeholder="e.g., Undergoing treatment, Discharged" className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1995" />
                    </div>
                  </>}
              </div>

              <div className="mt-8" data-id="element-1996">
                <button onClick={handleNext} disabled={claimType === 'death' ? !formData.deceasedName || !formData.dateOfDeath || !formData.relationship : !formData.disabilityDesc || !formData.incidentDate || !formData.medicalStatus} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition-all" data-id="element-1997">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* STEP 2: Document Upload */}
          {step === 2 && <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-1998">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1999">
                Upload Documents
              </h2>
              <p className="text-sm text-gray-500 mb-6" data-id="element-2000">
                Please upload clear copies of the following required documents.
              </p>

              <div className="space-y-4 flex-1" data-id="element-2001">
                {claimType === 'death' ? <>
                    <UploadItem title="Death Certificate" desc="Official death certificate" uploaded={uploads.deathCert} onUpload={() => setUploads({
                ...uploads,
                deathCert: true
              })} data-id="element-2002" />
                    <UploadItem title="Valid ID" desc="Deceased's valid ID" uploaded={uploads.validId} onUpload={() => setUploads({
                ...uploads,
                validId: true
              })} data-id="element-2003" />
                    <UploadItem title="Medical Reports" desc="Hospital/Doctor's report" uploaded={uploads.medicalReport} onUpload={() => setUploads({
                ...uploads,
                medicalReport: true
              })} data-id="element-2004" />
                  </> : <>
                    <UploadItem title="Disability Certificate" desc="Official medical certificate" uploaded={uploads.disabilityCert} onUpload={() => setUploads({
                ...uploads,
                disabilityCert: true
              })} data-id="element-2005" />
                    <UploadItem title="Valid ID" desc="Your valid ID" uploaded={uploads.validId} onUpload={() => setUploads({
                ...uploads,
                validId: true
              })} data-id="element-2006" />
                    <UploadItem title="Medical Reports" desc="Hospital/Doctor's report" uploaded={uploads.medicalReport} onUpload={() => setUploads({
                ...uploads,
                medicalReport: true
              })} data-id="element-2007" />
                  </>}
              </div>

              <div className="mt-8" data-id="element-2008">
                <button onClick={handleNext} disabled={claimType === 'death' ? !uploads.deathCert || !uploads.validId || !uploads.medicalReport : !uploads.disabilityCert || !uploads.validId || !uploads.medicalReport} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition-all" data-id="element-2009">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* STEP 3: Review */}
          {step === 3 && <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-2010">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-2011">
                Review Claim
              </h2>
              <p className="text-sm text-gray-500 mb-6" data-id="element-2012">
                Please review your claim details before submitting.
              </p>

              <div className="space-y-6 flex-1" data-id="element-2013">
                {/* Summary */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2014">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2015">
                    Claim Summary
                  </h3>
                  <div className="space-y-3" data-id="element-2016">
                    <div className="flex justify-between" data-id="element-2017">
                      <span className="text-sm text-gray-500" data-id="element-2018">Claim Type</span>
                      <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2019">
                        {claimType === 'death' ? 'Death Claim' : 'Permanent Disability'}
                      </span>
                    </div>
                    {claimType === 'death' ? <>
                        <div className="flex justify-between" data-id="element-2020">
                          <span className="text-sm text-gray-500" data-id="element-2021">
                            Deceased Name
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2022">
                            {formData.deceasedName}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-2023">
                          <span className="text-sm text-gray-500" data-id="element-2024">
                            Date of Death
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2025">
                            {formData.dateOfDeath}
                          </span>
                        </div>
                      </> : <>
                        <div className="flex justify-between" data-id="element-2026">
                          <span className="text-sm text-gray-500" data-id="element-2027">
                            Incident Date
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2028">
                            {formData.incidentDate}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-2029">
                          <span className="text-sm text-gray-500" data-id="element-2030">
                            Medical Status
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2031">
                            {formData.medicalStatus}
                          </span>
                        </div>
                      </>}
                  </div>
                </div>

                {/* Uploaded Files */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2032">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2033">
                    Uploaded Files
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-2034">
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold" data-id="element-2035">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-2036" /> Valid ID
                    </span>
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold" data-id="element-2037">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-2038" /> Medical Report
                    </span>
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold" data-id="element-2039">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-2040" />{' '}
                      {claimType === 'death' ? 'Death Cert' : 'Disability Cert'}
                    </span>
                  </div>
                </div>

                {/* Payout Destination */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2041">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2042">
                    Payout Destination
                  </h3>
                  <div className="flex items-center gap-3" data-id="element-2043">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-[#0D3B3B]" data-id="element-2044">
                      <UserIcon className="w-5 h-5" data-id="element-2045" />
                    </div>
                    <div data-id="element-2046">
                      <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2047">
                        Skydd Wallet
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2048">
                        Default payout destination
                      </p>
                    </div>
                  </div>
                </div>

                {/* Declaration */}
                <label className="flex items-start gap-3 p-4 bg-amber-50 rounded-2xl cursor-pointer border border-amber-100" data-id="element-2049">
                  <input type="checkbox" checked={acceptedDeclaration} onChange={e => setAcceptedDeclaration(e.target.checked)} className="mt-1 w-5 h-5 rounded border-amber-300 text-amber-500 focus:ring-amber-500" data-id="element-2050" />
                  <span className="text-xs text-amber-800 font-medium leading-relaxed" data-id="element-2051">
                    I declare that all information and documents provided are
                    true and accurate to the best of my knowledge. I understand
                    that false claims may lead to legal action.
                  </span>
                </label>
              </div>

              <div className="mt-8" data-id="element-2052">
                <button onClick={handleNext} disabled={!acceptedDeclaration || isSubmitting} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition-all flex items-center justify-center gap-2" data-id="element-2053">
                  {isSubmitting ? <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" data-id="element-2054" /> : 'Submit Claim'}
                </button>
              </div>
            </motion.div>}

          {/* STEP 4: Success & Tracking */}
          {step === 4 && <motion.div key="step4" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex-1 flex flex-col" data-id="element-2055">
              <div className="text-center mb-8" data-id="element-2056">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500 mx-auto mb-4 border-4 border-white shadow-lg shadow-emerald-500/20" data-id="element-2057">
                  <CheckCircleIcon className="w-10 h-10" data-id="element-2058" />
                </div>
                <h2 className="text-2xl font-black text-[#0D3B3B] mb-2" data-id="element-2059">
                  Claim Submitted!
                </h2>
                <p className="text-sm text-gray-500 leading-relaxed px-4" data-id="element-2060">
                  Your claim has been successfully submitted and is now under
                  review. We're here to support you through this process.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-2061">
                <div className="flex justify-between items-center pb-4 border-b border-gray-50 mb-6" data-id="element-2062">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2063">
                    Reference
                  </span>
                  <span className="text-sm font-mono font-bold text-[#0D3B3B]" data-id="element-2064">
                    CLM-20260514001
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-50 mb-6" data-id="element-2065">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2066">
                    Expected Review
                  </span>
                  <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2067">
                    3 - 5 Business Days
                  </span>
                </div>

                {/* Visual Timeline */}
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2068">
                  Claim Status
                </h3>
                <div className="relative pl-6 space-y-6" data-id="element-2069">
                  <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gray-100" data-id="element-2070" />

                  <div className="relative" data-id="element-2071">
                    <div className="absolute -left-6 w-6 h-6 bg-emerald-500 rounded-full border-4 border-white flex items-center justify-center" data-id="element-2072">
                      <CheckCircleIcon className="w-3 h-3 text-white" data-id="element-2073" />
                    </div>
                    <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2074">
                      Submitted
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2075">May 14, 2026</p>
                  </div>

                  <div className="relative" data-id="element-2076">
                    <div className="absolute -left-6 w-6 h-6 bg-amber-500 rounded-full border-4 border-white flex items-center justify-center" data-id="element-2077">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" data-id="element-2078" />
                    </div>
                    <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2079">
                      Under Review
                    </p>
                    <p className="text-xs text-amber-600 font-medium" data-id="element-2080">
                      Currently processing
                    </p>
                  </div>

                  <div className="relative opacity-50" data-id="element-2081">
                    <div className="absolute -left-6 w-6 h-6 bg-gray-200 rounded-full border-4 border-white" data-id="element-2082" />
                    <p className="text-sm font-bold text-gray-500" data-id="element-2083">
                      Insurer Review
                    </p>
                  </div>

                  <div className="relative opacity-50" data-id="element-2084">
                    <div className="absolute -left-6 w-6 h-6 bg-gray-200 rounded-full border-4 border-white" data-id="element-2085" />
                    <p className="text-sm font-bold text-gray-500" data-id="element-2086">Approved</p>
                  </div>

                  <div className="relative opacity-50" data-id="element-2087">
                    <div className="absolute -left-6 w-6 h-6 bg-gray-200 rounded-full border-4 border-white" data-id="element-2088" />
                    <p className="text-sm font-bold text-gray-500" data-id="element-2089">Paid</p>
                  </div>
                </div>
              </div>

              <div className="mt-auto" data-id="element-2090">
                <button onClick={() => navigate('policies')} className="w-full py-4 rounded-xl font-bold text-lg bg-white border border-gray-200 text-[#0D3B3B] shadow-sm hover:bg-gray-50 active:scale-95 transition-all" data-id="element-2091">
                  Return to Policies
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}
function UploadItem({
  title,
  desc,
  uploaded,
  onUpload
}: {
  title: string;
  desc: string;
  uploaded: boolean;
  onUpload: () => void;
}) {
  return <div className={`p-4 rounded-2xl border flex items-center justify-between transition-colors ${uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`} data-id="element-2092">
      <div className="flex items-center gap-3" data-id="element-2093">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${uploaded ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-2094">
          <FileTextIcon className="w-5 h-5" data-id="element-2095" />
        </div>
        <div data-id="element-2096">
          <h4 className="font-bold text-sm text-[#0D3B3B]" data-id="element-2097">{title}</h4>
          <p className="text-xs text-gray-500" data-id="element-2098">{desc}</p>
        </div>
      </div>
      {uploaded ? <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center" data-id="element-2099">
          <CheckCircleIcon className="w-5 h-5" data-id="element-2100" />
        </div> : <button onClick={onUpload} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-[#0D3B3B] text-xs font-bold rounded-lg transition-colors flex items-center gap-1" data-id="element-2101">
          <UploadCloudIcon className="w-4 h-4" data-id="element-2102" /> Upload
        </button>}
    </div>;
}